import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import HelpAside from "../components/HelpAside";
import wizMarket from "../../../assets/image/home/wizMarketLogo.png"

const API = process.env.REACT_APP_FASTAPI_ADS_URL;
const UPLOAD_BASE =
    process.env.REACT_APP_UPLOAD_PUBLIC_BASE_URL || "https://wizmarket.ai/uploads";
const EMAIL_DOMAINS = ["선택안함", "naver.com", "gmail.com", "nate.com", "daum.net"];
const PAGE_SIZE = 10;

const Inquiry = () => {
    // 폼 상태
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [emailId, setEmailId] = useState("");
    const [emailDomain, setEmailDomain] = useState("");
    const [emailPick, setEmailPick] = useState("선택안함");
    const [loading, setLoading] = useState(false);
    const [, setErrorMsg] = useState("");
    const [list, setList] = useState([]);
    const [hasQueried, setHasQueried] = useState(false);
    const [openDetail, setOpenDetail] = useState(false);
    const [current, setCurrent] = useState(null);
    const [page, setPage] = useState(1);
    const [previewImage, setPreviewImage] = useState(null); // { url, filename } | null

    // 이메일 도메인 픽커
    useEffect(() => {
        if (emailPick !== "선택안함") setEmailDomain(emailPick);
    }, [emailPick]);

    // 정규화
    // 첨부파일 정규화: attachment1~3 + origin1~3 사용
    const normalizeAttachments = (raw) => {
        const list = [];

        const pushOne = (path, origin) => {
            if (!path) return;

            const rawUrl = String(path);
            const isAbsolute = /^https?:\/\//.test(rawUrl);

            // /app/uploads/ 로 저장된 경우 잘라내기
            let pathPart = rawUrl;
            if (rawUrl.startsWith("/app/uploads/")) {
                pathPart = rawUrl.replace("/app/uploads/", "");
            }
            // DB에 help/.... 로만 들어온 경우도 처리
            pathPart = pathPart.replace(/^\/+/, ""); // 앞 슬래시 제거

            const fullUrl = isAbsolute ? rawUrl : `${UPLOAD_BASE}/${pathPart}`;

            const filename =
                origin && String(origin).trim()
                    ? String(origin).trim()
                    : (pathPart.split("/").pop() || "첨부파일");

            list.push({
                url: fullUrl,      // 실제 이미지 URL
                filename,         // 화면에 보여줄 이름(origin 우선)
            });
        };

        // 새로운 구조: attachment1~3 + origin1~3
        for (let i = 1; i <= 3; i += 1) {
            pushOne(raw[`attachment${i}`], raw[`origin${i}`]);
        }

        // 혹시 백엔드에서 attachments 배열을 같이 내려주는 경우도 합쳐줌
        if (Array.isArray(raw.attachments)) {
            raw.attachments.forEach((a) => {
                if (!a) return;
                if (typeof a === "string") {
                    pushOne(a, null);
                } else {
                    pushOne(a.url || a.path, a.origin || a.filename);
                }
            });
        }

        // 예전 필드(file1~3)도 혹시 남아 있다면
        ["file1", "file2", "file3"].forEach((k) => {
            if (raw[k]) pushOne(raw[k], null);
        });

        return list;
    };

    // 정규화
    const normalize = (raw) => {
        const id = raw.id ?? raw.help_id ?? raw.inquiry_id ?? raw.ticket_id ?? raw.uuid;
        const category = raw.category ?? raw.category_name ?? raw.type ?? "미분류";
        const created_at = raw.created_at ?? raw.createdAt ?? raw.inserted_at ?? raw.created ?? null;
        const answer = raw.answer ?? raw.reply ?? "";
        const answered_at = raw.answered_at ?? raw.reply_at ?? null;
        const status = raw.status ?? (answer && String(answer).trim() ? "answered" : "pending");
        const name = raw.name ?? raw.user_name ?? "";
        const email = raw.email ?? "";
        const phone = raw.phone ?? "";
        const contentRaw = String(raw.content ?? raw.message ?? raw.body ?? "");
        let title = raw.title ?? raw.subject ?? "";
        if (!title) {
            const firstLine = contentRaw.replace(/\r\n/g, "\n").trimStart().split("\n")[0] || "";
            title = firstLine.slice(0, 30) || "(제목 없음)";
        }

        // 🔹 여기: 첨부파일 + origin 정규화
        const attachments = normalizeAttachments(raw);

        return {
            id,
            category,
            created_at,
            answer,
            answered_at,
            status,
            name,
            email,
            phone,
            title,
            content: contentRaw,
            attachments,
            _raw: raw,
        };
    };



    // 프리뷰: 제목이 내용 첫 줄에서 파생된 경우 목록에서는 1행 제거
    const previewOf = (row) => {
        const txt = String(row.content || "").replace(/\r\n/g, "\n").trimStart();
        const hasServerTitle = !!(row._raw?.title || row._raw?.subject);
        if (hasServerTitle) return txt;
        const i = txt.indexOf("\n");
        return i >= 0 ? txt.slice(i + 1).trimStart() : "";
    };

    // 조회
    const fetchAndFilter = useCallback(async () => {
        setErrorMsg("");
        setHasQueried(true);
        setLoading(true);
        setOpenDetail(false);
        setCurrent(null);
        try {
            const res = await fetch(`${API}/help/list/app`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ name: name.trim(), phone: phone.trim() }),
            });
            const ct = res.headers.get("content-type") || "";
            if (!res.ok || !ct.includes("application/json")) {
                throw new Error("목록을 불러오지 못했습니다.");
            }
            const arr = await res.json();
            const normalized = arr.map(normalize);

            const nm = name.trim().toLowerCase();
            // 🔹 사용자 입력 전화번호: 숫자만 남기기
            const phoneNorm = (phone || "").replace(/\D/g, "");

            const since = Date.now() - 31 * 24 * 60 * 60 * 1000;
            const filtered = normalized.filter((x) => {
                const byDate = x.created_at ? new Date(x.created_at).getTime() >= since : true;
                const byName = nm ? (x.name || "").toLowerCase() === nm : true;

                // 🔹 DB에 저장된 phone도 숫자만 남겨서 비교
                const rowPhoneNorm = (x.phone || "").replace(/\D/g, "");
                const byPhone = phoneNorm ? rowPhoneNorm === phoneNorm : true;

                return byDate && byName && byPhone;
            });

            setList(filtered);
            console.log(filtered)
            setPage(1);
        } catch (e) {
            console.error(e);
            setErrorMsg(e.message || "조회 중 오류가 발생했습니다.");
            setList([]);
        } finally {
            setLoading(false);
        }
    }, [name, phone]);

    // 페이지네이션
    const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return list.slice(start, start + PAGE_SIZE);
    }, [list, page]);

    // 필수 입력값(이름/이메일 아이디/도메인) 모두 채워졌는지
    const canQuery = useMemo(() => {
        const nm = name.trim();
        const ph = phone.trim();
        return nm.length > 0 && ph.length > 0;
    }, [name, phone]);

    const formatDate = (d) => {
        if (!d) return "-";
        const dt = new Date(d);
        if (isNaN(dt)) return "-";
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, "0");
        const day = String(dt.getDate()).padStart(2, "0");
        const hh = String(dt.getHours()).padStart(2, "0");
        const mm = String(dt.getMinutes()).padStart(2, "0");
        return `${y}-${m}-${day} ${hh}:${mm}`;
    };

    return (
        <div className="min-h-screen bg-white">
            <header className="max-w-6xl mx-auto top-0 z-50">
                <div className="h-15 px-5 py-3 flex items-center justify-between">
                    <a href="/" className="flex items-center gap-2 pl-6">
                        <img src={wizMarket} alt="wizMarket" className="h-8 w-auto" />
                        <p style={{ fontFamily: "SonsieOne" }}>wiz Market</p>
                    </a>
                    <div className="flex items-center gap-2">
                        <a href="/help" className="px-8 py-2 inline-flex items-center rounded-md bg-black/10 text-lg hover:bg-black/30">
                            고객센터
                        </a>
                        {/* <div className="px-4 py-2 inline-flex items-center text-white rounded-md bg-[#7864F9]/60 text-lg font-medium hover:bg-[#7864F9]">
                            로그인/회원가입
                        </div> */}
                    </div>
                </div>
            </header>
            <div className="max-w-6xl mx-auto px-6 grid grid-cols-12 gap-8 pt-8">
                {/* 좌측 사이드 */}
                <HelpAside activeKey="inquiry" />

                {/* 메인 */}
                <main className="col-span-12 lg:col-span-9 pb-12">
                    <h1 className="text-2xl font-extrabold tracking-tight">1:1 문의 조회</h1>
                    <p className="text-gray-500 pt-2 pb-6">
                        접수하신 상담 내역을 조회 합니다. 접수 내용은 수정이 불가합니다.
                        <br /> (접수 시점으로부터 1개월이 경과된 내역은 개인정보 보호에 의해 조회되지 않을 수 있습니다.)
                    </p>

                    {/* 검색 폼 */}
                    <section className="rounded-2xl border p-6">
                        <div className="grid grid-cols-12 gap-4">
                            <label className="col-span-12 md:col-span-2 flex items-center text-sm font-semibold text-gray-700">
                                <span className="text-red-500 pr-1">*</span> 이름
                            </label>
                            <div className="col-span-12 md:col-span-10">
                                <input
                                    className="w-full h-11 px-4 border rounded-lg"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder=""
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-12 gap-4 pt-4">
                            <label className="col-span-12 md:col-span-2 flex items-center text-sm font-semibold text-gray-700">
                                <span className="text-red-500 pr-1">*</span> 연락처
                            </label>
                            <div className="col-span-12 md:col-span-10">
                                <input
                                    className="w-full h-11 px-4 border rounded-lg"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="휴대폰 번호를 입력하세요 (예: 01012345678 또는 010-1234-5678)"
                                />
                            </div>
                        </div>


                        <div className="flex justify-center pt-6">
                            <button
                                type="button"
                                onClick={fetchAndFilter}
                                className={`h-11 px-8 rounded-full text-white ${canQuery && !loading ? "bg-violet-600 hover:bg-violet-700" : "bg-gray-300 cursor-not-allowed"
                                    }`}
                                disabled={loading || !canQuery}
                            >
                                {loading ? "조회 중..." : "조회"}
                            </button>
                        </div>
                    </section>

                    {/* 결과 목록 */}
                    {hasQueried && (
                        <section className="mt-8">
                            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                                <table className="w-full table-auto">
                                    <colgroup>
                                        <col className="w-24" />
                                        <col className="w-40" />
                                        <col />
                                        <col className="w-32" />
                                        <col className="w-32" />
                                    </colgroup>
                                    <thead className="bg-gray-50 text-gray-600 text-sm">
                                        <tr>
                                            <th className="text-center font-medium px-4 py-3">상태</th>
                                            <th className="text-center font-medium px-4 py-3">카테고리</th>
                                            <th className="text-center font-medium px-4 py-3">내용</th>
                                            <th className="text-center font-medium px-4 py-3">접수일</th>
                                            <th className="text-center font-medium px-4 py-3">답변일</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm text-gray-700">
                                        {loading ? (
                                            <tr>
                                                <td colSpan={5} className="px-4 py-10 text-center text-gray-500">불러오는 중…</td>
                                            </tr>
                                        ) : paged.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="px-4 py-10 text-center text-gray-500">조회 결과가 없습니다.</td>
                                            </tr>
                                        ) : (
                                            paged.map((row) => (
                                                <tr
                                                    key={row.id}
                                                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                                                    onClick={() => { setCurrent(row); setOpenDetail(true); }}
                                                    tabIndex={0}
                                                    role="button"
                                                >
                                                    <td className="px-4 py-3 text-center align-top">
                                                        <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs ${row.status === "answered" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                                                            }`}>
                                                            {row.status === "answered" ? "답변완료" : "대기"}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-center align-top">
                                                        <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700">
                                                            {row.category}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 align-top">
                                                        <div className="text-gray-900 font-medium line-clamp-1" title={row.title}>
                                                            {row.title}
                                                        </div>
                                                        <div className="text-gray-600 line-clamp-2 whitespace-pre-line">
                                                            {previewOf(row)}
                                                        </div>
                                                        <div className="text-xs text-gray-400 pt-1">
                                                            {row.name} · {row.email} {row.phone ? `· ${row.phone}` : ""}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-center align-top text-gray-500">{formatDate(row.created_at)}</td>
                                                    <td className="px-4 py-3 text-center align-top text-gray-500">
                                                        {row.answered_at ? formatDate(row.answered_at) : "-"}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* 페이지네이션 */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 mt-4">
                                    <button className="px-2 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50"
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page === 1}>이전</button>
                                    {Array.from({ length: totalPages }).map((_, i) => (
                                        <button key={i}
                                            className={`w-8 h-8 rounded-lg text-sm border transition ${page === i + 1 ? "bg-violet-600 text-white border-violet-600" : "border-gray-200 hover:bg-gray-100"
                                                }`}
                                            onClick={() => setPage(i + 1)}>{i + 1}</button>
                                    ))}
                                    <button className="px-2 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50"
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}>다음</button>
                                </div>
                            )}
                        </section>
                    )}

                    {/* 상세 패널 */}
                    {openDetail && current && (
                        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
                            <div className="w-[760px] max-w-[92vw] bg-white rounded-2xl shadow-xl overflow-hidden">
                                <div className="flex items-center justify-between px-4 py-3 border-b">
                                    <h3 className="text-lg font-semibold">문의 상세</h3>
                                    <button onClick={() => setOpenDetail(false)} className="px-3 py-1.5 rounded-md border hover:bg-gray-50">닫기</button>
                                </div>
                                <div className="max-h-[70vh] overflow-y-auto p-4 space-y-4">
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div><span className="text-gray-500">상태</span> : {current.status === "answered" ? "답변완료" : "대기"}</div>
                                        <div><span className="text-gray-500">카테고리</span> : {current.category}</div>
                                        <div><span className="text-gray-500">이름</span> : {current.name}</div>
                                        <div><span className="text-gray-500">이메일</span> : {current.email}</div>
                                        <div><span className="text-gray-500">연락처</span> : {current.phone || "-"}</div>
                                        <div><span className="text-gray-500">접수일</span> : {formatDate(current.created_at)}</div>
                                        <div><span className="text-gray-500">답변일</span> : {current.answered_at ? formatDate(current.answered_at) : "-"}</div>
                                    </div>
                                    <div className="border rounded-lg p-3">
                                        <div className="text-sm text-gray-500 mb-1">문의 내용</div>
                                        <div className="whitespace-pre-line">{current.content || "-"}</div>
                                    </div>
                                    {current.attachments?.length > 0 && (
                                        <div className="border rounded-lg p-3">
                                            <div className="text-sm text-gray-500 mb-2">첨부파일</div>
                                            <div className="flex flex-col gap-2">
                                                {current.attachments.map((a, idx) => (
                                                    <button
                                                        key={idx}
                                                        type="button"
                                                        onClick={() => setPreviewImage(a)} // { url, filename }
                                                        className="text-left text-violet-600 hover:underline break-all underline-offset-2"
                                                    >
                                                        {a.filename}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="border rounded-lg p-3">
                                        <div className="text-sm text-gray-500 mb-2">답변</div>
                                        <div className="whitespace-pre-line min-h-[4rem]">
                                            {current.answer?.trim() ? current.answer : "아직 답변이 준비되지 않았습니다."}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </main>
                {previewImage && (
                    <div
                        className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center"
                        onClick={() => setPreviewImage(null)}
                    >
                        <div
                            className="max-w-[90vw] max-h-[90vh]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={previewImage.url}
                                alt={previewImage.filename}
                                className="max-w-full max-h-[80vh] rounded-lg shadow-2xl bg-black"
                            />
                            <div className="mt-2 text-center text-sm text-gray-100 break-all">
                                {previewImage.filename}
                            </div>
                            <div className="mt-3 text-center">
                                <button
                                    type="button"
                                    onClick={() => setPreviewImage(null)}
                                    className="inline-flex items-center px-4 py-1.5 rounded-md bg-white text-sm text-gray-800 shadow hover:bg-gray-100"
                                >
                                    닫기
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

/** 간단 드롭다운(페이지 로컬) */
const Dropdown = ({ value, onChange, options = [], placeholder = "선택안함", className = "" }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);
    return (
        <div ref={ref} className={`relative ${className}`}>
            <button type="button" className="w-full h-11 px-4 border rounded-lg text-left flex items-center justify-between"
                onClick={() => setOpen((v) => !v)}>
                <span className={value ? "text-gray-900" : "text-gray-400"}>{value || placeholder}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" className="opacity-70">
                    <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
                </svg>
            </button>
            {open && (
                <div className="absolute z-10 w-full rounded-xl border bg-white shadow-lg py-2 top-12">
                    {options.map((opt) => (
                        <button key={opt} type="button"
                            className={`w-full text-left px-4 py-2 hover:bg-gray-50 ${opt === value ? "text-violet-600 font-medium" : "text-gray-700"}`}
                            onClick={() => { onChange(opt); setOpen(false); }}>
                            {opt}
                        </button>
                    ))}
                </div>
            )}

        </div>
    );
};

export default Inquiry;
